import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteFirmwareVersion,
  getFirmwares,
  listFirmwareVersion,
  updateFirmwareVersion,
  uploadFirmware,
} from "~/api/firmware";
import type {
  UpdateFirmwareParams,
  UploadFirmwareParams,
} from "~/api/firmware";
import type { SortDirection } from "~/api/types";
import { usePaginatedQuery } from "./usePaginatedQuery";

export const useGetFirmwares = (currentPage: number, pageSize: number) => {
  const {
    query: { data: firmwaresData, isLoading: isLoadingFirmwares },
    totalItems: totalFirmwares,
  } = usePaginatedQuery({
    queryFn: () =>
      getFirmwares({
        page: currentPage,
        per_page: pageSize,
      }),
    queryKey: [
      "getFirmwares",
      {
        currentPage,
        per_page: pageSize,
      },
    ],
  });

  return {
    firmwaresData,
    isLoadingFirmwares,
    totalFirmwares,
  };
};

export const useFirmwareVersions = (
  firmwareId: number,
  page: number,
  per_page: number,
  search: string,
  order_by: string,
  sort_direction: SortDirection,
) => {
  const params = {
    firmwareId,
    page,
    per_page,
    search,
    order_by,
    sort_direction,
  };
  const { data, isLoading } = useQuery({
    queryKey: ["firmwareVersions", params],
    queryFn: async () => listFirmwareVersion(params),
  });
  return {
    data,
    isLoading,
  };
};

export const useUploadFirmware = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UploadFirmwareParams) => uploadFirmware(params),
    onSuccess: async (_, variables) => {
      // Invalidate all firmware versions queries for this firmware
      await queryClient.invalidateQueries({
        queryKey: ["firmwareVersions"],
        predicate: (query) => {
          const [, params] = query.queryKey;
          return !!(
            params &&
            typeof params === "object" &&
            "firmwareId" in params &&
            params.firmwareId === variables.firmwareId
          );
        },
      });
    },
  });
};

export const useUpdateFirmwareVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateFirmwareParams) => updateFirmwareVersion(params),
    onSuccess: async (_, variables) => {
      // Invalidate all firmware versions queries for this firmware
      await queryClient.invalidateQueries({
        queryKey: ["firmwareVersions"],
        predicate: (query) => {
          const [, params] = query.queryKey;
          return !!(
            params &&
            typeof params === "object" &&
            "firmwareId" in params &&
            params.firmwareId === variables.firmwareId
          );
        },
      });
    },
  });
};

export const useDeleteFirmwareVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      firmwareId,
      firmwareVersionId,
    }: {
      firmwareId: number;
      firmwareVersionId: number;
    }) => deleteFirmwareVersion(firmwareId, firmwareVersionId),
    onSuccess: async (_, variables) => {
      // Invalidate all firmware versions queries for this firmware
      await queryClient.invalidateQueries({
        queryKey: ["firmwareVersions"],
        predicate: (query) => {
          const [, params] = query.queryKey;
          return !!(
            params &&
            typeof params === "object" &&
            "firmwareId" in params &&
            params.firmwareId === variables.firmwareId
          );
        },
      });
    },
  });
};
